<template>
  <main class="ai-page">
    <header class="ai-header">
      <button
        class="ai-icon-button"
        aria-label="返回设备页面"
        @click="$router.replace(returnPath($route.query.from))"
      >
        <ai-icon name="back" />
      </button>
      <div class="ai-brand">
        <span class="ai-mini-orb"><ai-icon name="spark" /></span
        ><span><span class="ai-brand-title"><ai-name /><span v-if="pageState.copy.versionBadge" class="ai-version-badge">{{ pageState.copy.versionBadge }}</span></span><small>让空间，听懂你的话</small></span>
      </div>
      <div class="ai-header-actions">
        <button
          class="ai-icon-button"
          aria-label="新建对话"
          :disabled="!caps.enabled || !!initialBlock || busy"
          @click="newConversation"
        >
          <ai-icon name="plus" />
        </button>
      </div>
    </header>
    <div class="ai-layout">
      <ai-navigation :settings-open="settingsOpen" @settings="openSettings" @close-settings="settingsOpen = false" :busy="busy" :disabled="!initialized || !caps.enabled || !!initialBlock" :history-open="showHistory" :open="quotaOpen" :loading="quotaLoading" :quota="quota" :overview="quotaOverview" :quota-disabled="!initialized || (!quotaOverviewSupported && (!caps.enabled || !!initialBlock))" :summary="quotaSummary" :copy="pageState.copy" :error="quotaError" @history="toggleHistory" @quota="openQuota" @refresh="loadQuota" @close="quotaOpen = false" />
      <button v-if="showHistory" class="ai-history-backdrop" aria-label="关闭历史对话" @click="toggleHistory" />
      <aside v-if="showHistory" ref="sessionList" @scroll.passive="loadSessionsNearBottom" class="ai-history" tabindex="-1" @keydown.esc="toggleHistory">
        <div class="ai-history-heading"><h2>最近对话</h2></div>
        <ai-notice v-if="sessionsLoading && !sessionList.length" message="正在加载对话…" />
        <ai-notice v-else-if="sessionsError" :message="sessionsError" notice-role="alert"><template #actions><button class="ai-quiet-button" @click="loadSessions(!!sessionsCursor)">重试</button></template></ai-notice>
        <ai-notice v-else-if="!sessionList.length" message="还没有历史对话" />
        <div
          v-for="item in sessionList"
          :key="item.sessionId"
          class="ai-history-item"
          :class="{ 'is-current': session && session.sessionId === item.sessionId }"
        >
          <button :disabled="busy" :aria-current="session && session.sessionId === item.sessionId ? 'true' : null" @click="openHistory(item)">
            <span class="ai-history-title" :title="item.title">{{ item.title || '新对话' }}</span><small>{{
              item.createdAtText || ''
            }}</small></button
          ><button
            :disabled="busy"
            aria-label="删除这段对话"
            @click="removeSession(item)"
          >
            ×
          </button>
        </div>
        <ai-notice v-if="sessionsLoading && sessionList.length" message="正在加载更多对话…" />
        <button
          v-if="sessionsCursor"
          class="ai-quiet-button"
          :disabled="busy"
          @click="loadSessions(true)"
        >
          更多对话
        </button>
      </aside>
      <section ref="main" class="ai-main" :class="{ 'ai-main-welcome': !turns.length }">
        <div
          ref="scroll"
          class="ai-scroll"
          :class="{ 'ai-scroll-restoring': restoringScroll }"
          @scroll.passive="updateScrollPosition"
          aria-live="polite"
          aria-atomic="false"
        >
          <div v-if="turns.length && (historyCursor || historyPaged)" class="ai-history-loader" role="status">
            <button v-if="historyCursor" class="ai-quiet-button" :disabled="historyLoading" @click="moreHistory">{{ historyLoading ? '正在加载…' : historyError ? '加载失败，点击重试' : '加载更早消息' }}</button>
            <span v-else>已加载全部消息</span>
          </div>
          <section v-if="initialBlock" class="ai-welcome ai-access-state" role="status">
            <div class="ai-orb"><ai-icon name="spark" /></div>
            <h1>{{ initialBlock.title || '暂时无法使用 Ai助理' }}</h1>
            <p>{{ initialBlock.message }}</p>
            <p v-if="initialBlock.remainingTokens != null" class="ai-access-detail">{{ initialBlock.reason === 'FAMILY_QUOTA_EXHAUSTED' ? '家庭共享' : '个人' }}剩余额度：{{ Number(initialBlock.remainingTokens).toLocaleString() }} Token</p>
            <p v-if="initialBlock.resetText" class="ai-access-detail">{{ initialBlock.resetText }}</p>
            <button class="ai-primary" :disabled="initializing" @click="bootstrap">{{ initializing ? '正在检查…' : '重新检查' }}</button>
          </section>
          <section v-else-if="!turns.length && !pendingRequest" class="ai-welcome">
            <div class="ai-orb"><ai-icon name="spark" /></div>
            <span class="ai-eyebrow">你的专属Ai助理</span>
            <h1>{{ pageState.copy.welcomeTitle }}</h1>
            <p>{{ pageState.copy.welcomeDescription }}</p>
            <div class="ai-suggestions">
              <button v-for="suggestion in pageState.copy.suggestions || []" :key="suggestion.text" :disabled="!initialized || !caps.enabled || !!initialBlock" @click="useSuggestion(suggestion.text)">
                <ai-icon name="spark" /><strong>{{ suggestion.label }}</strong><small>{{ suggestion.text }}</small><span>↗</span>
              </button>
            </div>
            <div class="ai-scope">
              <ai-icon name="shield" /><span
                >{{ pageState.copy.scope }}</span
              >
            </div>
          </section>
          <section v-for="turn in turns" :key="turn.turnId" class="ai-turn">
            <div v-if="turn.inputText" class="ai-user-message" @mouseenter="now = Date.now()">
              <p class="ai-user-bubble">{{ turn.inputText }}</p>
              <div class="ai-user-tools"><time v-if="turn.createdAt" class="ai-message-time" :datetime="turn.createdAt" :title="messageTime(turn.createdAt, now).full">{{ messageTime(turn.createdAt, now).label }}</time>
              <button class="ai-copy" aria-label="复制这条消息" title="复制消息" @click="copyText(turn.inputText, turn.turnId + ':user')"><ai-icon :name="copiedKey === turn.turnId + ':user' ? 'check' : 'copy'" /></button></div>
            </div>
            <div class="ai-answer-label">
              <span class="ai-mini-orb"><ai-icon name="spark" /></span>Ai助理<span
                v-if="turn.readOnly"
                class="ai-history-label"
                >历史记录</span
              >
            </div>
            <div class="ai-answer" @mouseenter="now = Date.now()">
              <ai-card
                v-for="card in turn.components"
                :key="card.id"
                :card="card"
                :blocked="turn.blocked"
                :busy="busy"
                :action-busy="activeActionKey === cardRequestKey(turn, card)"
                :pagination="pagination[cardRequestKey(turn, card)]"
                :read-only="turn.readOnly"
                :now="now"
                @action="submitAction(turn, $event)"
                @page="nextPage(turn, $event)"
              />
              <div class="ai-feedback">
                <button class="ai-copy" aria-label="复制Ai助理回复" title="复制回复" @click="copyAnswer(turn)"><ai-icon :name="copiedKey === turn.turnId + ':assistant' ? 'check' : 'copy'" /></button>
                <template v-if="terminal(turn) || turn.state === 'WAITING_ACTION'">
                <button title="有帮助" aria-label="有帮助" :aria-pressed="ratingOf(turn) === 'LIKE'" :disabled="feedbackBusy" @click="toggleFeedback(turn, 'LIKE')"><ai-icon name="like" /></button>
                <button title="需要改进" aria-label="没有帮助" :aria-pressed="ratingOf(turn) === 'DISLIKE'" :disabled="feedbackBusy" @click="toggleFeedback(turn, 'DISLIKE')"><ai-icon name="dislike" /></button>
                <time v-if="turn.finishedAt" class="ai-message-time" :datetime="turn.finishedAt" :title="messageTime(turn.finishedAt, now).full">{{ messageTime(turn.finishedAt, now).label }}</time>
                <ai-notice v-if="ratingOf(turn)" :message="feedbackReceipt(turn)" />
                </template>
              </div>
              <section v-if="feedbackOpen === turn.turnId" ref="feedbackPanel" class="ai-feedback-panel" aria-label="补充反馈">
                <div class="ai-feedback-heading"><strong>{{ feedbackTitle }}</strong><button aria-label="关闭反馈面板" @click="feedbackOpen = null">×</button></div>
                <p class="ai-feedback-prompt">{{ feedbackCopy.prompt }}</p>
                <div class="ai-feedback-reasons">
                  <button v-for="reason in feedbackReasons" :key="reason.code" :disabled="feedbackBusy" :aria-pressed="feedbackReason.includes(reason.code)" @click="feedbackReason = feedbackReason.includes(reason.code) ? feedbackReason.filter(code => code !== reason.code) : feedbackReason.concat(reason.code)">{{ reason.label }}</button>
                </div>
                <textarea v-model="feedbackComment" maxlength="500" rows="2" aria-label="反馈补充说明" :placeholder="feedbackCopy.placeholder" :disabled="feedbackBusy" />
                <ai-notice v-if="feedbackError" :message="feedbackError" notice-role="alert" />
                <div class="ai-feedback-actions"><button @click="feedbackOpen = null">{{ feedbackCopy.skip }}</button><button class="ai-feedback-submit" :disabled="feedbackBusy || !hasFeedbackDetail" @click="feedback(turn, 'DISLIKE', feedbackReason, feedbackComment, true)">{{ feedbackBusy ? feedbackCopy.saving : feedbackCopy.submit }}</button></div>
              </section>
            </div>
          </section>

          <section v-if="pendingRequest" class="ai-turn" aria-live="polite">
            <div class="ai-user-message"><p class="ai-user-bubble">{{ pendingRequest.text }}</p></div>
            <template v-if="sending">
              <div class="ai-answer-label"><span class="ai-mini-orb"><ai-icon name="spark" /></span>Ai助理</div>
              <div class="ai-answer"><ai-execution-state status="NOT_STARTED" :progress="true" label="正在处理，请稍候" /></div>
            </template>
            <p v-else class="ai-submission-status" role="status">发送结果待确认，请重试原请求</p>
          </section>
        </div>
        <footer v-show="!initialBlock" ref="composerWrap" class="ai-composer-wrap">
          <button v-if="turns.length && !nearBottom" class="ai-jump" aria-label="回到最新消息" title="回到最新消息" @click="scrollToLatest"><ai-icon name="arrow-down" /></button>
          <ai-notice v-if="error" :message="error" notice-role="alert"><template #actions>
            <button v-if="!initialized" :disabled="initializing" @click="bootstrap">重新加载</button>
            <button v-if="pendingRequest" :disabled="busy" @click="send">
              重试原请求</button
            ><button
              v-else-if="latest && !latest.readOnly"
              :disabled="busy"
              @click="refresh(latest, true)"
            >
              {{ errorCode === 'AUTH_UNAVAILABLE' ? '刷新重试' : '刷新结果' }}
            </button>
          </template></ai-notice>
          <div v-if="initialized && caps.enabled && !consented" class="ai-consent">
            <div class="ai-consent-scroll" tabindex="0" role="region" aria-label="使用说明"><ai-notice :message="pageState.copy.consent" /></div>
            <button class="ai-primary" @click="acceptConsent">
              同意并开始
            </button>
          </div>
          <template v-else>
            <ai-notice v-if="historyMode" :message="pageState.copy.historyNotice"><template #actions><button
                class="ai-quiet-button ai-new-conversation-link"
                :disabled="busy"
                @click="newConversation"
              >
                新建对话
              </button>
            </template></ai-notice>
            <ai-notice v-if="copyNotice" :message="copyNotice" />
            <ai-notice v-if="quotaNotice" :message="quotaNotice.text"><template #actions><button @click="openQuota">{{ quotaNotice.actionLabel }}</button></template></ai-notice>
            <ai-notice v-if="speechNotice" class="ai-speech-notice" :message="speechNotice" />
            <form class="ai-composer" :class="{ 'is-speech-active': speechActive }" @submit.prevent="send">
              <textarea
                ref="composer"
                :aria-hidden="speechActive ? 'true' : 'false'"
                v-model="draft"
                aria-label="告诉Ai助理你想做什么"
                :placeholder="latest && latest.state === 'WAITING_ACTION' ? pageState.copy.waitingPlaceholder : pageState.copy.placeholder"
                rows="1"
                maxlength="2000"
                :disabled="
                  speechActive || !initialized || !caps.enabled ||
                    busy ||
                    pendingRequest !== null ||
                    historyMode
                "
                @keydown.enter.exact.prevent="onEnter"
              />
              <speech-input :disabled="!initialized || !caps.enabled || busy || pendingRequest !== null || historyMode || canStop" @active="speechActive = $event" @capture="captureSpeechCursor" @recognized="insertSpeech" @notice="onSpeechNotice" />
              <button v-if="canStop" type="button" class="ai-send" :disabled="busy" :aria-label="composerLabel" :title="composerLabel" @click="cancel"><span class="ai-stop-symbol" /></button>
              <button v-else type="submit" class="ai-send" :disabled="speechActive || !canSend" :aria-label="composerLabel" :title="composerLabel"><ai-icon name="send" /></button>
            </form>
            <div v-if="initialized && !caps.modelEnabled" class="ai-composer-meta" role="status">
              <span>当前 AI 服务暂不可用，请稍后重试。</span>
              <button type="button" :disabled="initializing || busy" @click="bootstrap">刷新服务状态</button>
            </div>

            <ai-notice v-if="!caps.controlEnabled" message="当前设备控制尚未开放。" />
          </template>
        </footer>
      </section>
    </div>
  </main>
</template>
<script>
import SpeechInput from '@/speech/SpeechInput.vue'
import { createSpeechAdoptionReporter } from '@/integration/speech-ai'
import { hasConsent, saveConsent, returnPath, initializeTabSession, voiceDirectSend } from '@/ai/state/preferences'
import { messageTime } from '@/ai/presentation/message-time'
import { normalizeQuotaOverview } from '@/integration/quota-overview'
import { quotaView, quotaWarning } from '@/ai/presentation/quota'
import AiNotice from './components/AiCompatibilityNotice.vue'
import AiName from '@/components/content/brand/AiName.vue'
import AiIcon from '@/components/common/icons/AiIcon.vue'
import AiCard from './components/AiCard.vue'
import AiExecutionState from './components/AiExecutionState.vue'
import AiNavigation from './components/AiNavigation.vue'
import { installAiGestureGuard } from '@/ai/presentation/gesture-guard'
import {
  CAPABILITIES,
  TERMINAL,
  acceptSnapshot,
  validateSnapshot
} from '@/ai/protocol/contract'
import {
  capabilities,
  createAiClient,
  errorMessage,
  identity,
  requestId
} from '@/ai/services/api'

import { feedbackCopy, pickFeedbackTitle, reasonCodes } from '@/ai/presentation/feedback-copy'

// 所有新消息共用准入条件，待发送文字不依赖输入框状态。
function canSubmitText(page, text) {
  return (
    page.caps.enabled && page.caps.modelEnabled &&
    page.initialized && page.consented &&
    !page.busy && !page.historyMode && !page.pendingRequest &&
    typeof text === 'string' && text.trim() && text.length <= 2000 &&
    (!page.latest || page.terminal(page.latest) || page.latest.state === 'WAITING_ACTION')
  )
}

export default {
  name: 'AiPage',
  components: { SpeechInput, AiName, AiIcon, AiCard, AiExecutionState, AiNavigation, AiNotice },
  provide() { return { aiPageState: this.pageState } },
  data() {
    return {
      caps: capabilities(),
      pageState: { copy: {}, valid: true },
      initialized: false,
      initializing: false,
      restoringScroll: false,
      sessionsLoaded: false,
      draft: '', speechActive: false, speechNotice: '', speechRefs: [], speechMixed: false,
      turns: [],
      session: null,
      sessionList: [],
      sessionsLoading: false,
      sessionsError: '',
      sessionsCursor: null,
      showHistory: false,
      historyMode: false,
      historyCursor: null,
      historyPaged: false,
      historyLoading: false,
      historyError: false,
      sending: false,
      stopping: false,
      actionSubmitting: false,
      paging: false,
      sessionChanging: false,
      activeActionKey: null,
      pagination: {},
      nearBottom: true,
      error: '',
      errorCode: '',
      initialBlock: null,
      copyNotice: '',
      copiedKey: '',
      quota: null,
      quotaLoading: false,
      quotaOpen: false,
      quotaOverview: null,
      quotaOverviewSupported: false,
      settingsOpen: false,
      quotaSummary: null,
      quotaError: '',
      consented: false,
      pendingRequest: null,
      cardActionTurnId: null,
      sessionRequest: null,
      ratings: {},
      feedbackDetails: {},
      feedbackOpen: null,
      feedbackCopy,
      feedbackTitle: '',
      feedbackReason: [],
      feedbackComment: '',
      feedbackBusy: false,
      feedbackError: '', 
      feedbackReasons: [
        { code: 'NOT_UNDERSTOOD', label: '没理解我的意思' },
        { code: 'WRONG_RESULT', label: '结果不准确' },
        { code: 'TOO_SLOW', label: '响应太慢' }
      ],
      now: Date.now()
    }
  },
  computed: {
    quotaNotice() { return quotaWarning(this.quotaSummary) },
    hasFeedbackDetail() { return !!(this.feedbackReason.length || this.feedbackComment.trim()) },
    latest() {
      return this.turns[this.turns.length - 1]
    },
    cardOwnsProgress() {
      return !!(this.latest && ((this.cardActionTurnId && this.cardActionTurnId === this.latest.turnId) || (this.latest.components || []).some(card => card.type === 'action_list')))
    },
    // Keep the shared interaction lock, but never use it to choose a progress icon.
    // Only message/cards own progress; unrelated requests must not change stop into send.
    busy() {
      return !!(this.sending || this.stopping || this.actionSubmitting || this.paging || this.sessionChanging)
    },
    canStop() {
      return !this.pendingRequest && !this.cardOwnsProgress && !this.historyMode && !!(this.latest && !this.latest.readOnly && this.latest.canCancel === true && ['QUEUED', 'GENERATING'].includes(this.latest.state))
    },
    composerLabel() {
      if (this.sending) return '正在提交消息'
      if (this.stopping) return '正在请求停止'
      if (this.pendingRequest) return '请求结果尚未确认，请重试原请求'
      if (this.canStop) return this.busy ? '请等待当前操作完成后停止' : '停止处理'
      if (this.historyMode) return '新建对话后可发送消息'
      if (this.busy) return '请等待当前操作完成'
      if (this.latest && !this.latest.readOnly && !this.terminal(this.latest) && this.latest.state !== 'WAITING_ACTION') return '正在处理，请在消息中查看进度'
      return '发送消息'
    },
    canSend() {
      return canSubmitText(this, this.draft)
    }
  },
  watch: {
    quotaOpen(open) {
      if (!open) { clearTimeout(this._quotaRefreshTimer); this._quotaRefreshPending = false }
    },
    '$store.state.token'(token) {
      if (!token) {
        this.stop()
        this.draft = ''; this.speechRefs = []; this.speechNotice = ''
        this.pendingSpeech = null; this.pendingRequest = null
      }
    },
    draft() { this.$nextTick(this.resizeComposer) }
  },
  updated() {
    if (!this._composerResize) this.measureComposer()
  },
  mounted() {
    this._api = createAiClient()
    this._quotaApi = createAiClient('/usage/v1')
    this._identity = identity()
    this._alive = true
    this._removeGestureGuard = installAiGestureGuard(this.$el)
    this._visibility = () => {
      clearTimeout(this._poll)
      if (document.hidden) { clearTimeout(this._expiryTimer); return }
      if (!this._alive) return
      this.armExpiry()
      if (this.shouldPoll(this.latest)) this.refresh(this.latest)

    }
    document.addEventListener('visibilitychange', this._visibility)
    this.$nextTick(() => this.measureComposer())
    if (typeof ResizeObserver !== 'undefined') {
      this._composerResize = new ResizeObserver(() => this.measureComposer())
      this._composerResize.observe(this.$refs.composerWrap)
    } else window.addEventListener('resize', this.measureComposer)
    this.bootstrap()
  },
  beforeDestroy() {
    this.stop()
    if (this._removeGestureGuard) this._removeGestureGuard()
    if (this._composerResize) this._composerResize.disconnect()
    window.removeEventListener('resize', this.measureComposer)
    document.removeEventListener('visibilitychange', this._visibility)
  },
  methods: {
    returnPath,
    acceptConsent() {
      if (this.consented) return
      saveConsent(this.caps.consentVersion)
      this.consented = true
    },
    messageTime,
    measureComposer() {
      if (!this._alive || !this.$refs.main || !this.$refs.composerWrap) return
      const height = Math.ceil(this.$refs.composerWrap.getBoundingClientRect().height)
      if (height === this._composerHeight) return
      this._composerHeight = height
      this.$refs.main.style.setProperty('--ai-composer-clearance', (height + 24) + 'px')
    },
    updateScrollPosition() {
      const box = this.$refs.scroll
      this.nearBottom = !box || box.scrollHeight - box.scrollTop - box.clientHeight < 80
      if (box && box.scrollTop < 60 && !this.restoringScroll && this.historyCursor && !this.historyError) this.moreHistory()
    },
    scrollToLatest() {
      const box = this.$refs.scroll
      if (box) box.scrollTop = box.scrollHeight
      this.nearBottom = true
    },
    resizeComposer() {
      const input = this.$refs.composer
      if (!input) return
      input.style.height = 'auto'
      input.style.height = Math.min(input.scrollHeight, 116) + 'px'
    },
    toggleHistory() {
      if (this.busy) return
      this.quotaOpen = false
      this.settingsOpen = false
      this.showHistory = !this.showHistory
      if (this.showHistory) {
        this.loadSessions()
        this.$nextTick(() => { const close = this.$el.querySelector('.ai-history'); if (close) close.focus() })
      } else this.$nextTick(() => { const trigger = this.$el.querySelector('[aria-label="历史对话"]'); if (trigger) trigger.focus() })
    },
    copyAnswer(turn) {
      const lines = []
      for (const card of turn.components) {
        if (card.text) lines.push(card.text)
        if (card.title) lines.push(card.title)
        if (card.description) lines.push(card.description)
        if (card.targetLabel) lines.push([card.operationLabel, card.targetLabel].filter(Boolean).join(' · '))
        for (const item of card.items || []) lines.push(card.type === 'info_list' ? item.label + '：' + item.value : [item.subtitle, item.title, item.statusText].filter(Boolean).join(' · '))
        for (const item of card.entries || card.parameters || []) lines.push(item.label + '：' + item.value)
        if (card.impact) lines.push(card.impact)
      }
      return this.copyText(lines.join('\n'), turn.turnId + ':assistant')
    },
    async copyText(value, key) {
      if (!value) return
      let copied = false
      if (navigator.clipboard && window.isSecureContext) {
        try { await navigator.clipboard.writeText(value); copied = true } catch (_) { /* 继续使用用户手势复制降级。 */ }
      }
      if (!copied) {
        const active = document.activeElement
        const area = document.createElement('textarea')
        area.value = value
        area.style.cssText = 'position:fixed;left:-9999px;top:0;user-select:text'
        document.body.appendChild(area)
        try { area.focus(); area.select(); copied = document.execCommand('copy') } catch (_) { copied = false }
        finally { area.remove(); if (active && active.focus) active.focus() }
      }
      this.copiedKey = copied ? key : ''
      this.copyNotice = copied ? '已复制' : '复制未成功，请选中文字复制。'
      clearTimeout(this._copyTimer)
      this._copyTimer = setTimeout(() => { this.copiedKey = ''; this.copyNotice = '' }, 1800)
    },
    resumeKey() {
      return (
        'aiSession:v2:' + (sessionStorage.getItem('aiLoginEpoch') || '') + ':' +
        identity()
          .split('|')
          .slice(0, 2)
          .join('|')
      )
    },
    async bootstrap() {
      if (this.initializing) return
      this.initializing = true
      try {
        initializeTabSession()
        const sessionId = sessionStorage.getItem(this.resumeKey())
        const result = await this.call('page/bootstrap', sessionId ? { sessionId, capabilities: CAPABILITIES } : {})
        const restriction = result.availability && !result.availability.available ? result.availability : null
        this.initialBlock = !this.turns.length ? restriction : null
        this.quotaOverviewSupported = result.quotaOverviewSupported === true
        this.caps = result.capabilities
        this.consented = hasConsent(this.caps.consentVersion)
        this.pageState.copy = result.copy || {}
        this.initialized = true
        this.errorCode = ''
        this.error = (restriction && this.turns.length ? restriction.message : '') || result.resumeError || ''
        if (result.session) {
          this.restoringScroll = true
          this.session = result.session
          this.historyCursor = result.session.nextCursor || null
          this.historyPaged = false
          this.historyError = false
          this.consented = hasConsent(this.caps.consentVersion)
          for (const turn of result.session.items) this.apply(turn)
          // Position after message/composer layout, before revealing restored content.
          await this.$nextTick()
          this.measureComposer()
          await this.$nextTick()
          this.scrollToLatest()
        } else if (sessionId && !restriction) sessionStorage.removeItem(this.resumeKey())
      } catch (error) {
        this.fail(error)
        if (this._alive && !error.silent && !this.turns.length) this.initialBlock = { title: '暂时无法连接 AI 服务', message: errorMessage(error) }
      }
      finally { this.restoringScroll = false; this.initializing = false }
    },
    stop() {
      this.settingsOpen = false
      this._alive = false
      if (this._speechAdoption) this._speechAdoption.close()
      this.pageState.valid = false
      this.quotaOpen = false
      this.quota = null
      this.quotaOverview = null
      this.quotaSummary = null
      clearTimeout(this._quotaRefreshTimer)
      this._quotaRefreshPending = false
      clearTimeout(this._poll)
      clearTimeout(this._expiryTimer)
      this._pollEpoch = (this._pollEpoch || 0) + 1
      clearTimeout(this._copyTimer)
      if (this._api) this._api.close()
      if (this._quotaApi) this._quotaApi.close()
    },
    terminal(turn) {
      return TERMINAL.includes(turn.state)
    },
    async call(path, body) {
      const result = await this._api.post(path, body)
      if (!this._alive) throw new Error('AI_PAGE_CLOSED')
      if (Object.prototype.hasOwnProperty.call(result, 'quotaSummary')) this.adoptQuotaSummary(result.quotaSummary, path === 'quota/get')
      return result
    },
    fail(error) {
      if (this._alive) {
        if (error.message === 'TOKEN_ERROR' || error.message === 'ACCOUNT_CHANGED') this.stop()
        this.errorCode = error.message || ''
        this.error = errorMessage(error)

      }
    },
    onEnter(event) {
      if (!event.isComposing && event.keyCode !== 229) this.send()
    },
    useSuggestion(text) {
      if (this.busy || !this.initialized || !this.caps.enabled) return
      if (!this.consented) {
        this.$toast('请先同意使用说明，再使用 Ai助理', 3500)
        return
      }
      this.send(text)
    },
    onSpeechNotice(message, popup = false) {
      this.speechNotice = popup ? '' : message
      if (popup && message) this.$toast(message, 3500)
    },
    captureSpeechCursor() {
      const input = this.$refs.composer
      this.speechSelection = { start: input.selectionStart, end: input.selectionEnd }
    },
    insertSpeech(result) {
      if (!result.text || !result.text.trim()) return
      const hadDraft = this.draft.length > 0 || this.speechRefs.length > 0
      const directSend = voiceDirectSend()
      const selection = this.speechSelection || { start: this.draft.length, end: this.draft.length }
      const next = this.draft.slice(0, selection.start) + result.text + this.draft.slice(selection.end)
      if (next.length > 2000) { this.onSpeechNotice('识别文字超过当前输入上限，请缩短录音后重试。', true); return }
      if (this.speechRefs.length >= 10) { this.onSpeechNotice('一条消息最多关联10段录音，请先发送当前文字。', true); return }
      const fillDraft = () => {
        this.speechMixed = this.speechMixed || hadDraft
        this.speechRefs.push(result.recognitionId); this.draft = next
        if (directSend && hadDraft) this.onSpeechNotice('已合并到草稿，请确认后发送', true)
        this.$nextTick(() => {
          const input = this.$refs.composer
          if (input) { input.focus(); input.setSelectionRange(selection.start + result.text.length, selection.start + result.text.length) }
        })
      }
      if (!directSend || hadDraft) { fillDraft(); return }
      // recognized 先于录音组件 finally 清理；等 active 事件传播后直接提交，不回填输入框。
      this.$nextTick(async () => {
        await this.$nextTick()
        if (!this._alive) return
        if (canSubmitText(this, result.text) && !this.speechActive) {
          this.send(result.text, { recognitionIds: [result.recognitionId], singleVoice: true })
        } else {
          fillDraft()
        }
      })
    },
    async send(text, speech = null) {
      const direct = typeof text === 'string'
      if (this.speechActive) return
      if ((direct ? !canSubmitText(this, text) : (!this.canSend && !this.pendingRequest)) || this.busy || !this._alive)
        return
      const submittedText = direct ? text.trim() : this.draft.trim()
      this.sending = true
      this.error = ''
      this._pollFailures = 0
      this._pollDelay = 1000
      this._pollDeadline = null
      try {
        if (!this.session && !this.sessionRequest) this.sessionRequest = {
          clientRequestId: requestId(), clientType: 'WEB', protocolMajor: 1, protocolMinor: 0,
          capabilities: CAPABILITIES, consentVersion: this.caps.consentVersion
        }
        if (!this.pendingRequest)
          this.pendingRequest = {
            ...(this.session ? { sessionId: this.session.sessionId } : { sessionRequest: this.sessionRequest }),
            clientRequestId: requestId(),
            text: submittedText,
            ...(this.latest && this.latest.state === 'WAITING_ACTION' ? { replyToTurnId: this.latest.turnId, expectedTurnRevision: this.latest.revision } : {})
          }
        this.$nextTick(this.scrollToLatest)
        if (!this.pendingSpeech) this.pendingSpeech = direct
          ? (speech || { recognitionIds: [], singleVoice: false })
          : { recognitionIds: (this.speechRefs || []).slice(), singleVoice: !this.speechMixed }
        const snapshot = await this.call('turn/create', this.pendingRequest)
        const association = { ...this.pendingSpeech, turnId: snapshot.turnId }
        if (!this.session) {
          this.session = { sessionId: snapshot.sessionId, state: 'OPEN' }
          sessionStorage.setItem(this.resumeKey(), snapshot.sessionId)
          this.sessionsLoaded = false
        }
        const previousId = this.pendingRequest.replyToTurnId
        if (previousId) {
          const previous = this.turns.find(t => t.turnId === previousId)
          if (previous) this.$set(this.turns, this.turns.indexOf(previous), { ...previous, readOnly: true })
        }
        this.apply(snapshot)
        this.$nextTick(this.scrollToLatest)
        this.pendingRequest = null; this.pendingSpeech = null
        this.draft = ''; this.speechRefs = []; this.speechMixed = false
        if (association.recognitionIds.length) {
          if (!this._speechAdoption) this._speechAdoption = createSpeechAdoptionReporter()
          // Result rendering/polling already started; statistics cannot hold the composer busy.
          this._speechAdoption.report(association)
        }
      } catch (error) {
        if (direct && this._alive && !['TOKEN_ERROR', 'ACCOUNT_CHANGED'].includes(error.message)) {
          this.draft = submittedText
          this.speechRefs = speech ? speech.recognitionIds.slice() : []
          this.speechMixed = speech ? !speech.singleVoice : false
        }
        if (error.definitive) { this.pendingRequest = null; this.pendingSpeech = null }
        this.fail(error)
      } finally {
        this.sending = false
        this.$nextTick(this.updateScrollPosition)
      }
    },
    apply(incoming, authoritative = false) {
      if (!this.session || this.session.sessionId !== incoming.sessionId) return
      const scroll = this.$refs.scroll
      const current = this.turns.find(t => t.turnId === incoming.turnId)
      // Executions and candidate pagination update the existing reply in place.
      // Do not pull the reader to the bottom on submit or subsequent polling.
      const actionUpdate = current && (current.components || []).some(c => c.type === 'action_list')
      const follow = !actionUpdate && (!scroll || scroll.scrollHeight - scroll.scrollTop - scroll.clientHeight < 120)
      // A manual full read may repair a same-revision local view; never replace a newer revision.
      const result = acceptSnapshot(authoritative && current && current.revision === incoming.revision ? null : current, incoming)
      const value = { ...result.snapshot, blocked: result.blocked }
      if (current) this.$set(this.turns, this.turns.indexOf(current), value)
      else this.turns.push(value)
      this.$nextTick(() => {
        if (follow && this.$refs.scroll)
          this.$refs.scroll.scrollTop = this.$refs.scroll.scrollHeight
      })
      this.armExpiry()
      this.schedule(value)
    },
    armExpiry() {
      clearTimeout(this._expiryTimer)
      this.now = Date.now()
      if (!this._alive || document.hidden) return
      const dates = this.turns.filter(t => !t.readOnly && t.state === 'WAITING_ACTION').flatMap(t => (t.components || []).map(c => c.interaction && Date.parse(c.interaction.expiresAt))).filter(n => n > this.now)
      if (dates.length) this._expiryTimer = setTimeout(() => this.armExpiry(), Math.min(2147483647, Math.min(...dates) - this.now + 10))
    },
    shouldPoll(turn) {
      return !!turn && !turn.readOnly && !this.terminal(turn) && turn.state !== 'WAITING_ACTION'
    },
    schedule(turn) {
      clearTimeout(this._poll)
      if (!this._alive || document.hidden || !this.shouldPoll(turn) || !this.latest || this.latest.turnId !== turn.turnId) return
      if (this._pollTurnId !== turn.turnId) { this._pollTurnId = turn.turnId; this._pollStartedAt = Date.now(); this._pollDelay = 1000 }
      const deadline = Date.parse(turn.deadlineAt) || (this._pollDeadline || (this._pollDeadline = Date.now() + 180000))
      if (Date.now() >= deadline) { this.errorCode = ''; this.error = this.pageState.copy.pollTimeout || '等待结果超时，请查询原任务进度，勿重复提交。'; return }
      const delay = Math.min(deadline - Date.now(), Math.max(1000, Math.min(5000, Math.max(turn.pollAfterMs || 1000, this._pollDelay || 1000))))
      this._poll = setTimeout(() => this.refresh(this.latest), delay)
    },
    async refresh(turn, manual = false) {
      if (!this._alive || !turn) return
      if (this._pollInFlight) { this.schedule(this.latest); return }
      if (this.busy) { this.schedule(this.latest); return }
      this._pollInFlight = true
      const epoch = this._pollEpoch || 0
      const sessionId = turn.sessionId
      const previousError = this.error
      try {
        // Explicit recovery reads the full snapshot; routine polling stays lightweight.
        const result = await this.call('turn/get', { sessionId, turnId: turn.turnId, ...(manual ? {} : { knownRevision: turn.revision }) })
        if (!this._alive || epoch !== (this._pollEpoch || 0) || !this.session || this.session.sessionId !== sessionId) return
        if (result.sessionId !== sessionId || result.turnId !== turn.turnId) throw new Error('WRONG_RESPONSE')
        this._pollFailures = 0
        // Do not erase a newer error raised by another operation while this read was pending.
        if (this.error === previousError && (manual || this._pollError === previousError)) this.error = ''
        this._pollError = null
        // Short foreground tasks poll every second after each response; never overlap requests.
        if (this._pollTurnId !== turn.turnId) { this._pollTurnId = turn.turnId; this._pollStartedAt = Date.now() }
        this._pollDelay = result.notModified && Date.now() - this._pollStartedAt >= 30000 ? 3000 : 1000
        if (!result.notModified) this.apply(result, manual)
        this.schedule(this.latest)
      } catch (error) {
        if (!this._alive || epoch !== (this._pollEpoch || 0) || !this.session || this.session.sessionId !== sessionId) return
        this.fail(error)
        this._pollError = this.error
        this._pollFailures = (this._pollFailures || 0) + 1
        if (!error.definitive && this._pollFailures < 3) { this._pollDelay = 5000; this.schedule(this.latest) }
      } finally { this._pollInFlight = false }
    },
    actionBody(turn, card) {
      const a = card.interaction
      return {
        sessionId: turn.sessionId,
        turnId: turn.turnId,
        actionId: a.actionId,
        actionToken: a.actionToken,
        expectedActionRevision: a.actionRevision
      }
    },
    cardRequestKey(turn, card) { return JSON.stringify([turn.sessionId, turn.turnId, card.id]) },
    async submitAction(turn, event) {
      if (this.busy || turn.blocked || turn.readOnly) return
      this.activeActionKey = this.cardRequestKey(turn, event.card)
      this.cardActionTurnId = turn.turnId
      this.actionSubmitting = true
      this.error = ''
      try {
        this.apply(
          await this.call('action/submit', {
            ...this.actionBody(turn, event.card),
            kind: event.kind,
            input: event.input
          })
        )
      } catch (error) {
        this.fail(error)
      } finally {
        this.actionSubmitting = false
        this.activeActionKey = null
        const current = this.turns.find(t => t.turnId === turn.turnId)
        if (current && !this.terminal(current)) this.schedule(current)
      }
    },
    async nextPage(turn, card) {
      if (this.busy || (!['device_cards', 'info_list', 'scene_cards'].includes(card.type) && (turn.blocked || turn.readOnly))) return
      const key = this.cardRequestKey(turn, card)
      this.$set(this.pagination, key, { loading: true, error: false })
      this.paging = true
      try {
        let component
        if (['device_cards', 'info_list', 'scene_cards'].includes(card.type)) {
          const page = await this.call('result/page', { sessionId: turn.sessionId, turnId: turn.turnId, revision: turn.revision, componentId: card.id, cursor: card.nextCursor })
          if (page.sessionId !== turn.sessionId || page.turnId !== turn.turnId || page.revision !== turn.revision || page.componentId !== card.id) throw new Error('WRONG_RESPONSE')
          component = { ...card, items: card.items.concat(page.items), hasMore: page.hasMore, nextCursor: page.nextCursor }
          if (['info_list','scene_cards'].includes(card.type)) {
            const unique = new Map(component.items.map(item => [item.id, item])); component.items = Array.from(unique.values())
            if (card.type === 'scene_cards') component.footerText = `已加载 ${component.items.length} 项`
          }
          if (Number.isInteger(page.matchedTotal)) {
            // 状态查询按页读取服务端当前记录；状态变化导致重复引用时保留最近一次数据。
            const key = item => JSON.stringify([item.ref.regionId, item.ref.productId, item.ref.deviceId || null])
            const unique = new Map(component.items.map(item => [key(item), item]))
            component.items = Array.from(unique.values())
            component.matchedTotal = page.matchedTotal
            component.footerText = `匹配 ${page.matchedTotal} 项 · 已加载 ${component.items.length} 项`
            component.truncated = page.truncated
            component.notice = page.notice
          }
        } else {
          const page = await this.call('action/page', { ...this.actionBody(turn, card), cursor: card.nextCursor })
          if (card.executionMode === 'explicit') {
            if (page.actionId !== card.interaction.actionId || !page.turn || page.turn.sessionId !== turn.sessionId || page.turn.turnId !== turn.turnId || page.actionRevision <= card.interaction.actionRevision) throw new Error('WRONG_RESPONSE')
            this.apply(page.turn); return
          }
          if (page.actionId !== card.interaction.actionId || page.actionRevision !== card.interaction.actionRevision) throw new Error('WRONG_RESPONSE')
          component = page.component
        }
        const index = this.turns.findIndex(t => t.turnId === turn.turnId && t.revision === turn.revision)
        if (index < 0) return
        const replacement = { ...turn, components: turn.components.map(c => c.id === card.id ? component : c) }
        replacement.blocked = validateSnapshot(replacement).blocked
        this.$set(this.turns, index, replacement)
      } catch (error) {
        if (['TOKEN_ERROR', 'ACCOUNT_CHANGED', 'AI_DISABLED', 'AI_ROLE_DISABLED'].includes(error.message)) this.fail(error)
        else if (this._alive) this.$set(this.pagination, key, { loading: false, error: true })
      } finally {
        if (this.pagination[key]) this.$set(this.pagination, key, { ...this.pagination[key], loading: false })
        this.paging = false
      }
    },
    async cancel() {
      if (!this.canStop || this.busy) return
      const turn = this.latest
      this.stopping = true
      this.error = ''
      clearTimeout(this._poll)
      this._pollEpoch = (this._pollEpoch || 0) + 1
      try {
        const result = await this.call('turn/cancel', { sessionId: turn.sessionId, turnId: turn.turnId })
        if (!this._alive || !this.session || this.session.sessionId !== turn.sessionId) return
        if (result.sessionId !== turn.sessionId || result.turnId !== turn.turnId) throw new Error('WRONG_RESPONSE')
        // Only the server can confirm cancellation; an already-dispatching action stays in progress.
        this.apply(result)
      } catch (error) {
        if (this._alive) this.fail(error)
      } finally {
        this.stopping = false
        if (this._alive) this.schedule(this.latest)
      }
    },
    async toggleFeedback(turn, rating) {
      if (this.feedbackBusy) return
      if (this.ratingOf(turn) === rating) return this.feedback(turn, 'NONE')
      if (rating === 'DISLIKE') return this.openFeedback(turn)
      return this.feedback(turn, rating)
    },
    ratingOf(turn) {
      return Object.prototype.hasOwnProperty.call(this.ratings, turn.turnId)
        ? this.ratings[turn.turnId]
        : turn.feedback && turn.feedback.rating
    },
    feedbackReceipt(turn) {
      if (this.ratingOf(turn) === 'LIKE') return this.feedbackCopy.likeSaved
      const detail = turn.feedback
      return detail && ((detail.reasonCodes && detail.reasonCodes.length) || detail.detail) ? this.feedbackCopy.detailSaved : this.feedbackCopy.dislikeSaved
    },
    async openFeedback(turn) {
      if (this.feedbackBusy) return
      if (this.feedbackOpen !== turn.turnId) this.feedbackTitle = pickFeedbackTitle(this.feedbackTitle)
      this.feedbackOpen = turn.turnId
      this.feedbackError = ''
      this.feedbackComment = (turn.feedback && turn.feedback.detail) || ''
      this.feedbackReason = reasonCodes(this.feedbackDetails[turn.turnId] || (turn.feedback && turn.feedback.reasonCodes))
      // Reveal the opened panel, including older replies; do not jump to an unrelated latest turn.
      this.$nextTick(() => {
        if (this.feedbackOpen !== turn.turnId) return
        const refs = this.$refs.feedbackPanel
        const panel = Array.isArray(refs) ? refs[0] : refs
        if (panel) panel.scrollIntoView({ block: 'end', behavior: 'auto' })
      })
      if (this.ratingOf(turn) !== 'DISLIKE') await this.feedback(turn, 'DISLIKE')
    },
    async feedback(turn, rating, selectedReasons = [], detail = '', close = false) {
      const reasons = reasonCodes(selectedReasons)
      if (this.feedbackBusy || (close && !reasons.length && !detail.trim())) return
      this.feedbackBusy = true
      this.feedbackError = ''
      try {
        await this.call('feedback/save', {
          sessionId: turn.sessionId, turnId: turn.turnId, rating, reasonCodes: reasons,
          detail: detail.trim() || null
        })
        this.$set(turn, 'feedback', rating === 'NONE' ? null : { rating, reasonCodes: reasons, detail: detail.trim() || null })
        this.$set(this.ratings, turn.turnId, rating === 'NONE' ? null : rating)
        this.$set(this.feedbackDetails, turn.turnId, reasons)
        if (close || rating === 'LIKE' || rating === 'NONE') this.feedbackOpen = null
      } catch (error) {
        this.feedbackError = errorMessage(error)
        if (this.feedbackOpen !== turn.turnId) this.fail(error)
      } finally {
        this.feedbackBusy = false
      }
    },
    adoptQuotaSummary(value, fromQuota = false) {
      if (!value || !Number.isFinite(value.asOf)) return
      const previous = this.quotaSummary
      if (previous && value.asOf < previous.asOf) return
      this.quotaSummary = value
      // Timestamps order snapshots; only a balance change/released reservation needs a refresh.
      if (!fromQuota && previous && (
        value.remainingTokens !== previous.remainingTokens ||
        value.reservedTokens < previous.reservedTokens
      )) this.queueQuotaRefresh()
    },
    queueQuotaRefresh() {
      if (!this._alive || !this.quotaOpen) return
      if (this.quotaLoading) { this._quotaRefreshPending = true; return }
      clearTimeout(this._quotaRefreshTimer)
      this._quotaRefreshTimer = setTimeout(() => {
        if (this._alive && this.quotaOpen) this.loadQuota()
      }, 0)
    },
    openSettings() {
      this.quotaOpen = false
      this.showHistory = false
      this.settingsOpen = !this.settingsOpen
    },
    openQuota() {
      this.settingsOpen = false
      this.quotaOpen = true
      this.showHistory = false
      if (this.quotaLoading) this._quotaRefreshPending = true
      else this.loadQuota()
    },
    async loadQuota() {
      if (this.quotaLoading) return
      clearTimeout(this._quotaRefreshTimer)
      this.quotaLoading = true
      this.quotaError = ''
      try {
        const result = this.quotaOverviewSupported
          ? await this._quotaApi.post('quota/get')
          : await this.call('quota/get')
        if (!this._alive) return
        if ((result.asOf || 0) >= (this._quotaStamp || 0)) {
          this.quotaOverview = this.quotaOverviewSupported ? normalizeQuotaOverview(result) : null
          this.quota = !this.quotaOverviewSupported && quotaView(result) ? result : null
          this._quotaStamp = result.asOf || 0
          if (this.quotaOverviewSupported || this.quota) this.adoptQuotaSummary(result.quotaSummary, true)
        }
        this._quotaReceivedAt = Date.now()
      } catch (error) {
        if (!this._alive) return
        this.quota = null
        this.quotaOverview = null
        this.quotaError = this.pageState.copy.quotaUnavailable || '额度暂不可用，请重试。'
        // 查询失败不保留旧余额，也不显示成0；额度问题不打断用户对话。
        if (error.message === 'TOKEN_ERROR' || error.message === 'ACCOUNT_CHANGED' || error.message === 'AI_DISABLED' || error.message === 'AI_ROLE_DISABLED') this.fail(error)
      } finally {
        this.quotaLoading = false
        if (this._quotaRefreshPending) {
          this._quotaRefreshPending = false
          this.queueQuotaRefresh()
        }
      }
    },
    loadSessionsNearBottom() {
      const box = this.$refs.sessionList
      if (this.showHistory && box && box.clientHeight > 0 && !this.sessionsError && this.sessionsCursor &&
          box.scrollHeight - box.scrollTop - box.clientHeight < 80) this.loadSessions(true)
    },
    async loadSessions(more = false) {
      if (!this.caps.enabled || this.sessionsLoading || (more && !this.sessionsCursor)) return
      if (!more && this.sessionsLoaded) {
        this.$nextTick(this.loadSessionsNearBottom)
        return
      }
      this.sessionsLoading = true
      this.sessionsError = ''
      try {
        const cursor = more ? this.sessionsCursor : null
        const result = await this.call('session/list', { pageSize: 20, ...(cursor ? { cursor } : {}) })
        const existing = more ? this.sessionList : []
        const known = new Set(existing.map(item => item.sessionId))
        this.sessionList = existing.concat(result.items.filter(item => !known.has(item.sessionId)))
        this.sessionsCursor = result.nextCursor && result.nextCursor !== cursor ? result.nextCursor : null
        this.sessionsLoaded = true
      } catch (error) {
        this.sessionsError = errorMessage(error)
      } finally {
        this.sessionsLoading = false
        this.$nextTick(this.loadSessionsNearBottom)
      }
    },
    async newConversation() {
      if (this.busy) return
      const closedSessionId = this.session && !this.historyMode ? this.session.sessionId : null
      this.sessionChanging = true
      try {
        if (this.session && !this.historyMode)
          await this.call('session/close', {
            sessionId: this.session.sessionId
          })
        clearTimeout(this._poll)
        clearTimeout(this._expiryTimer)
        this._pollEpoch = (this._pollEpoch || 0) + 1
        sessionStorage.removeItem(this.resumeKey())
        this.session = null
        this.turns = []
        this.pendingRequest = null; this.pendingSpeech = null
        this.sessionRequest = null
        this.historyMode = false
        this.historyCursor = null
        this.historyPaged = false
        this.error = ''
        this.draft = ''; this.speechRefs = []; this.speechMixed = false
        this.sessionList = this.sessionList.map(item => item.sessionId === closedSessionId ? { ...item, state: 'CLOSED' } : item)
      } catch (error) {
        this.fail(error)
      } finally {
        this.sessionChanging = false
      }
    },
    async openHistory(item) {
      if (this.busy) return
      this.sessionChanging = true
      try {
        let result = await this.call('session/get', {
          sessionId: item.sessionId, direction: 'older'
        })
        const canResume = result.canResume === true
        // The resume endpoint rechecks expiry/login under lock; never infer permission in the UI.
        if (canResume) result = await this.call('session/resume', { sessionId: item.sessionId, capabilities: CAPABILITIES })
        const rows = result.items.map(t => ({
          ...t,
          blocked: validateSnapshot(t).blocked
        }))
        clearTimeout(this._poll)
        clearTimeout(this._expiryTimer)
        this._pollEpoch = (this._pollEpoch || 0) + 1
        this.historyMode = !canResume
        this.showHistory = false
        this.session = result
        if (canResume) sessionStorage.setItem(this.resumeKey(), result.sessionId)
        else sessionStorage.removeItem(this.resumeKey())
        this.turns = rows
        this.historyCursor = result.nextCursor
        this.historyPaged = false
        this.historyError = false
        this.$nextTick(this.scrollToLatest)
        this.armExpiry()
        if (canResume && this.latest) this.schedule(this.latest)
        this.draft = ''; this.speechRefs = []; this.speechMixed = false
        this.sessionRequest = null
        this.pendingRequest = null; this.pendingSpeech = null
        this.error = ''
      } catch (error) {
        this.fail(error)
      } finally {
        this.sessionChanging = false
      }
    },
    async moreHistory() {
      if (this.historyLoading || this.sessionChanging || !this.session || !this.historyCursor) return
      const sessionId = this.session.sessionId
      const session = this.session
      const cursor = this.historyCursor
      this.historyLoading = true
      this.historyError = false
      try {
        const result = await this.call('session/get', { sessionId, cursor, direction: 'older' })
        if (!this._alive || this.session !== session || this.historyCursor !== cursor) return
        const box = this.$refs.scroll
        const height = box ? box.scrollHeight : 0
        const top = box ? box.scrollTop : 0
        const known = new Set(this.turns.map(t => t.turnId))
        const rows = result.items.filter(t => !known.has(t.turnId)).map(t => ({ ...t, blocked: validateSnapshot(t).blocked }))
        this.turns = rows.concat(this.turns)
        this.historyCursor = result.nextCursor
        this.historyPaged = true
        await this.$nextTick()
        if (box && this.session === session) box.scrollTop = top + box.scrollHeight - height
      } catch (error) {
        if (this.session === session) this.historyError = true
      } finally {
        this.historyLoading = false
      }
    },
    async removeSession(item) {
      if (this.busy) return
      if (
        !window.confirm(
          this.pageState.copy.deleteNotice || '删除这段对话？'
        )
      )
        return
      this.sessionChanging = true
      try {
        await this.call('session/delete', { sessionId: item.sessionId, capabilities: CAPABILITIES })
        if (this.session && item.sessionId === this.session.sessionId) {
          clearTimeout(this._poll)
          clearTimeout(this._expiryTimer)
          this._pollEpoch = (this._pollEpoch || 0) + 1
          sessionStorage.removeItem(this.resumeKey())
          this.session = null
          this.turns = []
          this.historyMode = false
          this.historyCursor = null
          this.historyPaged = false
          this.pendingRequest = null; this.pendingSpeech = null
          this.sessionRequest = null
        }
        this.sessionList = this.sessionList.filter(row => row.sessionId !== item.sessionId)
      } catch (error) {
        this.fail(error)
      } finally {
        this.sessionChanging = false
      }
    }
  }
}
</script>
<style scoped src="./page.css"></style>
